import { useState, useMemo, useRef, useEffect, ReactNode, useCallback } from 'react'
import Stack from '@mui/material/Stack'
import Card from '@mui/material/Card'
import Map from './mapLayers/ClimateMap'
import { Marker, Popup, ScaleControl, MapRef, Layer, Source, LayerProps } from 'react-map-gl'
import Typography from '@mui/material/Typography'
import { useLocation, useLocations } from '../hooks/locations'
import { usePortfolioAlerts } from '../hooks/alerts'
import Button from '@mui/material/Button'
import { Box, Chip, CircularProgress, Divider, alpha, darken, useTheme } from '@mui/material'
import ConfigProvider from 'balkerne-core/config'
import mapboxgl, { GeoJSONSource } from 'mapbox-gl'
import AlertTypeIcon from './AlertTypeIcon'
import SeverityIcon from './SeverityIcon'
import { DisplaySeverity } from 'balkerne-core/alerts'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import { ago } from 'balkerne-core/time'
import { useCurrentUser } from '../hooks/users'
import { useSelector } from 'react-redux'
import { RootState } from '../store'

export const PortfolioMap = ({ handleRedirect }) => {
  const mapRef = useRef<MapRef | null>(null)
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId })
  const locationIds = useMemo(() => locations?.map(location => location.id) ?? [], [locations])
  const { data: allAlerts, isLoading: isAlertsLoading } = usePortfolioAlerts({ only_active: true })
  const alerts = useMemo(
    () => allAlerts?.filter(alert => locationIds.includes(alert.property.id)),
    [allAlerts, locationIds],
  )

  const { data: user } = useCurrentUser()
  const alertsEnabled = user?.organisation?.features?.alerts ?? true
  const [isSatellite, setSatellite] = useState(false)
  const [isLoading, setLoading] = useState(false)

  const [entity, setEntity] = useState<'assets' | 'threats'>('assets')
  const [popupContent, setPopupContent] = useState<ReactNode>(null)
  const [coords, setPopupCoordinates] = useState<{ lat: number; lng: number } | null>(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null)
  const [previousCameraState, setPreviousCameraState] = useState<{
    center: [number, number]
    zoom: number
    bearing: number
    pitch: number
  } | null>(null)
  const { data: location } = useLocation(selectedPropertyId, { geojson: true })
  const theme = useTheme()

  const handleSelectProperty = useCallback(
    (propertyId: number, coords: { lat: number; lng: number }, children: ReactNode): void => {
      const map = mapRef.current?.getMap()
      if (!map) return

      map.flyTo({
        center: [coords.lng, coords.lat],
        zoom: 17,
        bearing: 30,
        pitch: 45,
        offset: [0, 100],
      })

      setPreviousCameraState({
        center: [map.getCenter().lng, map.getCenter().lat] as [number, number],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      })
      setSelectedPropertyId(propertyId)
      setPopupCoordinates(coords)
      setPopupContent(children)
    },
    [mapRef],
  )

  const handleClosePopup = useCallback(() => {
    setSelectedPropertyId(null)
    setPopupCoordinates(null)
    setPopupContent(null)
    const map = mapRef.current?.getMap()
    if (map !== undefined && previousCameraState !== null) {
      map.flyTo({ ...previousCameraState, speed: 3 })
    }
  }, [mapRef, previousCameraState])

  const handleViewProperty = useCallback(() => {
    if (selectedPropertyId) {
      handleRedirect(selectedPropertyId)
    }
  }, [selectedPropertyId])

  const handleClick = (e: mapboxgl.MapLayerMouseEvent) => {
    const map = mapRef.current?.getMap()
    if (e.features === undefined || e.features.length === 0 || map === undefined) return

    const feature = e.features[0]
    if (feature.source === 'portfolio-assets') {
      const mapboxSource = map.getSource('portfolio-assets') as GeoJSONSource

      // Clustered Layer
      if (feature.layer.id === 'clusters') {
        const clusterId = feature?.properties?.cluster_id
        if (clusterId === undefined) return
        mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return
          map.flyTo({
            // @ts-ignore
            center: feature.geometry.coordinates,
            zoom: zoom,
            speed: 3,
          })
        })
      } else if (feature.layer.id === 'unclustered-point') {
        const props = feature.properties
        console.log(props)
        if (props && props.id && props.latitude && props.longitude && props.name) {
          handleSelectProperty(
            props.id,
            {
              lat: props.latitude,
              lng: props.longitude,
            },
            <Stack minWidth={300}>
              <Typography variant="h6" fontWeight="bold">
                {props.name}
              </Typography>
              <Typography variant="subtitle2">
                {props.street}, {props.town}, {props.postcode}, {props.country}
              </Typography>
            </Stack>,
          )
        }
      }
    } else if (feature.source === 'risk-source') {
      const props = feature.properties
      if (props && props.id && props.name) {
        handleSelectProperty(
          props.id,
          {
            lat: props.latitude,
            lng: props.longitude,
          },
          <Stack minWidth={300}>
            <Typography variant="h6" fontWeight="bold">
              {props.name}
            </Typography>
          </Stack>,
        )
      }
    }
  }

  useEffect(() => {
    // Set to threats if any are active
    if (alertsEnabled && alerts?.length > 0) {
      setEntity('threats')
    } else {
      setEntity('assets')
    }
  }, [alerts, isAlertsLoading, alertsEnabled])

  return (
    <Card sx={{ position: 'relative', p: 0, m: 0 }}>
      <Box sx={{ height: { xs: 600, xl: 900 } }}>
        <Map
          reuseMaps
          mapNavigation
          ref={mapRef}
          onClick={handleClick}
          interactiveLayerIds={['clusters', 'unclustered-point', 'risk-layer']}
          initialViewState={{
            longitude: -2.3,
            latitude: 51.8,
            zoom: 6,
            pitch: 0,
            bearing: 0,
          }}
          style={{ width: '100%', height: '100%', borderRadius: '8px' }}
          mapStyle={
            isSatellite ? 'mapbox://styles/mapbox/satellite-v9' : 'mapbox://styles/balkerne/clmqen49o00bm01ns7ur41hzj'
          }
          mapboxAccessToken={ConfigProvider.mapboxPublicKey}
          mapOverlays={[
            {
              id: 'markers',
              type: 'choice',
              label: 'Markers',
              value: entity,
              options: [
                { value: 'assets', label: 'Assets' },
                ...(alertsEnabled ? [{ value: 'threats', label: 'Active threats' }] : []),
              ],
              onChange: value => setEntity(value as 'assets' | 'threats'),
            },
            { id: 'satellite', label: 'Satellite', checked: isSatellite, onChange: setSatellite },
          ]}
        >
          {/* Loader */}
          {isLoading && (
            <Stack
              justifyContent="center"
              alignItems="center"
              width="100%"
              height="100%"
              top={0}
              left={0}
              zIndex={1}
              position={'absolute'}
              bgcolor={alpha(theme.palette.background.paper, 0.5)}
            >
              <CircularProgress />
            </Stack>
          )}

          {/* Selected Asset */}
          {location?.geometry && (
            <Source id="select-property" type="geojson" data={location.geometry}>
              <Layer
                id="select-property-layer"
                type="fill-extrusion"
                paint={{
                  'fill-extrusion-color': 'red',
                  'fill-extrusion-height': 6,
                  'fill-extrusion-base': 0,
                  'fill-extrusion-opacity': 0.6,
                }}
              />
            </Source>
          )}

          {/* Popup */}
          {popupContent !== null && coords && (
            <Popup
              maxWidth="none"
              closeButton={true}
              anchor="bottom-left"
              closeOnClick={false}
              onClose={handleClosePopup}
              latitude={Number(coords.lat)}
              longitude={Number(coords.lng)}
            >
              {popupContent}
              <Divider sx={{ mb: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Button variant="outlined" size="small" onClick={handleClosePopup}>
                  Close
                </Button>
                <Button variant="contained" size="small" onClick={handleViewProperty}>
                  More <NavigateNextRoundedIcon />
                </Button>
              </Stack>
            </Popup>
          )}

          {/* Markers */}
          <Box>
            {mapRef.current?.getMap() != undefined && (
              <>
                {/* Alerts */}
                {entity === 'threats' && (
                  <ActiveThreatLayer
                    onClick={handleSelectProperty}
                    setLoading={setLoading}
                    map={mapRef.current.getMap()}
                  />
                )}
                {/* Properties */}
                {entity === 'assets' && (
                  <AssetLayer onClick={handleSelectProperty} setLoading={setLoading} map={mapRef.current.getMap()} />
                )}
              </>
            )}
          </Box>
          <ScaleControl position="bottom-right" />
        </Map>
      </Box>
    </Card>
  )
}

type CustomLayerProps = {
  map: mapboxgl.Map
  setLoading: (loading: boolean) => void
  onClick?: (propertyId: number, coords: { lat: number; lng: number }, children?: ReactNode) => void
}

const AssetLayer = ({ map, setLoading, onClick }: CustomLayerProps) => {
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations, isLoading } = useLocations({ group_id: groupId })
  if (!locations) return null

  useEffect(() => {
    setLoading(isLoading)
    return () => {
      setLoading(false)
    }
  }, [isLoading])

  const featureCollection = useMemo(() => {
    const features = locations.map(location => ({
      type: 'Feature',
      properties: {
        id: location.id,
        name: location.name,
        latitude: location.coordinates.latitude,
        longitude: location.coordinates.longitude,
        country: location.address.country,
        town: location.address.town,
        postcode: location.address.postcode,
        street: location.address.street,
      },
      geometry: {
        type: 'Point',
        coordinates: [location.coordinates.longitude, location.coordinates.latitude],
      },
    }))
    return {
      type: 'FeatureCollection',
      features: features,
    }
  }, [locations])

  const theme = useTheme()

  const clusterLayer: LayerProps = {
    id: 'clusters',
    type: 'circle',
    source: 'assets-source',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': [
        'step',
        ['get', 'point_count'],
        darken(theme.palette.primary.main, 0.05),
        20,
        darken(theme.palette.primary.main, 0.35),
        50,
        darken(theme.palette.primary.main, 0.7),
      ],
      'circle-radius': ['step', ['get', 'point_count'], 15, 20, 20, 50, 25],
    },
  }

  const clusterCountLayer: LayerProps = {
    id: 'cluster-count',
    type: 'symbol',
    source: 'assets-source',
    filter: ['has', 'point_count'],
    paint: {
      'text-color': 'white',
    },
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 12,
    },
  }

  const unclusteredPointLayer: LayerProps = {
    id: 'unclustered-point',
    type: 'circle',
    source: 'assets-source',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': theme.palette.primary.main,
      'circle-radius': 4,
      'circle-stroke-width': 1,
      'circle-stroke-color': 'white',
    },
  }

  const bounds = useMemo(() => {
    let minLat = -90
    let maxLat = 90
    let minLon = -180
    let maxLon = 180
    if (locations?.length) {
      minLat = locations[0].coordinates.latitude
      maxLat = locations[0].coordinates.latitude
      minLon = locations[0].coordinates.longitude
      maxLon = locations[0].coordinates.longitude
      for (const property of locations) {
        if (property.coordinates.latitude < minLat) {
          minLat = property.coordinates.latitude
        }
        if (property.coordinates.latitude > maxLat) {
          maxLat = property.coordinates.latitude
        }
        if (property.coordinates.longitude < minLon) {
          minLon = property.coordinates.longitude
        }
        if (property.coordinates.longitude > maxLon) {
          maxLon = property.coordinates.longitude
        }
      }
    } else {
      return undefined
    }
    return [minLon, minLat, maxLon, maxLat] as [number, number, number, number]
  }, [locations])

  useEffect(() => {
    if (map && bounds) {
      map.fitBounds(bounds, { padding: 50, screenSpeed: 3 })
    }
  }, [map, bounds])

  return (
    <Source
      id="portfolio-assets"
      type="geojson"
      data={featureCollection as any}
      cluster={true}
      clusterMaxZoom={8}
      clusterRadius={30}
    >
      <Layer {...clusterLayer} />
      <Layer {...clusterCountLayer} />
      <Layer {...unclusteredPointLayer} />
    </Source>
  )
}

const ActiveThreatLayer = ({ map, setLoading, onClick }: CustomLayerProps) => {
  const groupId = useSelector((state: RootState) => state.system.groupId)
  const { data: locations, isLoading: isLocationsLoading } = useLocations({ group_id: groupId })
  const locationIds = useMemo(() => locations?.map(location => location.id) ?? [], [locations])
  const { data: allAlerts, isLoading } = usePortfolioAlerts({ only_active: true })
  const alerts = useMemo(
    () => allAlerts?.filter(alert => locationIds.includes(alert.property.id)),
    [allAlerts, locationIds],
  )
  const theme = useTheme()
  if (!alerts) return null

  useEffect(() => {
    setLoading(isLoading)
    return () => {
      setLoading(false)
    }
  }, [isLoading])

  const bounds = useMemo(() => {
    let minLat = -90
    let maxLat = 90
    let minLon = -180
    let maxLon = 180
    if (alerts?.length) {
      minLat = alerts[0].property.coordinates.latitude
      maxLat = alerts[0].property.coordinates.latitude
      minLon = alerts[0].property.coordinates.longitude
      maxLon = alerts[0].property.coordinates.longitude
      for (const alert of alerts) {
        if (alert.property.coordinates.latitude < minLat) {
          minLat = alert.property.coordinates.latitude
        }
        if (alert.property.coordinates.latitude > maxLat) {
          maxLat = alert.property.coordinates.latitude
        }
        if (alert.property.coordinates.longitude < minLon) {
          minLon = alert.property.coordinates.longitude
        }
        if (alert.property.coordinates.longitude > maxLon) {
          maxLon = alert.property.coordinates.longitude
        }
      }
    } else {
      return undefined
    }
    return [minLon, minLat, maxLon, maxLat] as [number, number, number, number]
  }, [alerts])

  useEffect(() => {
    if (map && bounds) {
      map.fitBounds(bounds, { padding: 50, maxZoom: 8, screenSpeed: 3 })
    }
  }, [map, bounds])

  const handleClick = alert => {
    if (!onClick) return
    const node = (
      <Stack width={260}>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Chip
            avatar={
              <Stack direction="row" alignItems="center" justifyContent="center">
                <SeverityIcon severity={alert.severity} height={20} width={20} />
              </Stack>
            }
            label={DisplaySeverity[alert.severity]}
          />
          <Chip
            avatar={
              <Stack direction="row" alignItems="center" justifyContent="center">
                <AlertTypeIcon type={alert.type} height={20} width={20} />
              </Stack>
            }
            label={alert.type_display}
          />
        </Stack>
        <Divider sx={{ my: 1 }} />

        <Box maxHeight={200} overflow="auto">
          <Typography variant="body1" gutterBottom>
            <strong>{alert.title}</strong>
          </Typography>
          <Typography variant="body1">{alert.message}</Typography>
        </Box>

        <Typography variant="caption" mt={2}>
          Issued {ago(alert.created_at)}
        </Typography>
      </Stack>
    )
    const coords = {
      lat: alert.property.coordinates.latitude,
      lng: alert.property.coordinates.longitude,
    }
    onClick(alert.property.id, coords, node)
  }

  return alerts?.map(alert => (
    <Marker
      key={alert.id}
      style={{ cursor: 'pointer' }}
      longitude={alert.property.coordinates.longitude}
      latitude={alert.property.coordinates.latitude}
      anchor="center"
      scale={5}
      onClick={() => handleClick(alert)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          width: 40,
          height: 40,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'translate(-50%, -50%)',
            '@-webkit-keyframes ping': {
              '0%': {
                '-webkit-transform': 'scale(0.2)',
                transform: 'scale(0.2)',
                opacity: 0.8,
              },
              '80%': {
                '-webkit-transform': 'scale(1.2)',
                transform: 'scale(1.2)',
                opacity: 0,
              },
              '100%': {
                '-webkit-transform': 'scale(2.2)',
                transform: 'scale(2.2)',
                opacity: 0,
              },
            },
            '@keyframes ping': {
              '0%': {
                '-webkit-transform': 'scale(0.2)',
                transform: 'scale(0.2)',
                opacity: 0.8,
              },
              '80%': {
                '-webkit-transform': 'scale(1.2)',
                transform: 'scale(1.2)',
                opacity: 0,
              },
              '100%': {
                '-webkit-transform': 'scale(2.2)',
                transform: 'scale(2.2)',
                opacity: 0,
              },
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.severity[alert.severity],
            outline: '.5px solid grey',
            borderRadius: '50%',
            width: '100%',
            height: '100%',
            animation: 'ping 3s cubic-bezier(0, 0, 0.2, 1) infinite',
          }}
        />
        <Box
          sx={{
            height: '10px',
            width: '10px',
            borderRadius: '50%',
            bgcolor: theme.palette.severity[alert.severity],
            boxShadow: '0 2px 0.5px 0px rgba(0,0,0,.3)',
          }}
        ></Box>
      </Box>
    </Marker>
  ))
}

export default PortfolioMap
